import React from "react";

export default function AisprawlEndpointsChart({
  groups,
  getGroupKey,
  getGroupLabel,
  getEndpointLabel,
}) {
  const layoutRef = React.useRef(null);
  const activePillRef = React.useRef(null);
  const detailRef = React.useRef(null);

  const [selectedEndpoint, setSelectedEndpoint] = React.useState(() => {
    const firstGroup = groups?.[0];
    if (!firstGroup || !firstGroup.endpoints?.length) {
      return null;
    }
    return firstGroup.endpoints[0];
  });

  const [curvePath, setCurvePath] = React.useState("");
  const [verticalPaths, setVerticalPaths] = React.useState([]);

  React.useLayoutEffect(() => {
    const container = layoutRef.current;
    const pillEl = activePillRef.current;
    const detailEl = detailRef.current;

    if (!container || !pillEl || !detailEl || !selectedEndpoint) {
      setCurvePath("");
      setVerticalPaths([]);
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const pillRect = pillEl.getBoundingClientRect();
    const detailRect = detailEl.getBoundingClientRect();

    const startX = pillRect.right - containerRect.left - 1;
    const startY =
      pillRect.top - containerRect.top + pillRect.height * 0.5;
    const endX = detailRect.left - containerRect.left;
    const endY =
      detailRect.top - containerRect.top + detailRect.height * 0.5;

    const dx = endX - startX;
    const controlOffset = dx * 0.35;

    const path = [
      "M",
      startX,
      startY,
      "C",
      startX + controlOffset,
      startY,
      endX - controlOffset,
      endY,
      endX,
      endY,
    ].join(" ");

    setCurvePath(path);
    // Build vertical connector paths for each group
    const deviceColumns = container.querySelectorAll(
      ".aisprawl-endpoints-type-devices"
    );
    const newVerticals = [];

    deviceColumns.forEach((col) => {
      const buttons = col.querySelectorAll(
        ".aisprawl-endpoints-device-row-button"
      );
      if (!buttons.length) {
        return;
      }

      const firstRect = buttons[0].getBoundingClientRect();
      const lastRect =
        buttons[buttons.length - 1].getBoundingClientRect();
      const colRect = col.getBoundingClientRect();

      const x = colRect.left - containerRect.left;
      const topY =
        firstRect.top - containerRect.top + firstRect.height * 0.5;
      const bottomY =
        lastRect.top - containerRect.top + lastRect.height * 0.5;

      newVerticals.push(
        ["M", x, topY, "L", x, bottomY].join(" ")
      );
    });

    setVerticalPaths(newVerticals);
  }, [selectedEndpoint, groups]);

  return (
    <div ref={layoutRef} className="aisprawl-endpoints-layout">
      {curvePath && (
        <svg className="aisprawl-endpoints-curve-layer">
          <path
            d={curvePath}
            className="aisprawl-endpoints-curve-path"
          />
          {verticalPaths.map((vp, idx) => (
            <path
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              d={vp}
              className="aisprawl-endpoints-vertical-path"
            />
          ))}
        </svg>
      )}
      <div className="aisprawl-endpoints-by-type">
        {groups.map((group) => {
          const isSingleEndpoint = group.endpoints.length === 1;

          return (
            <div
              key={getGroupKey(group)}
              className="aisprawl-endpoints-type-row"
            >
              <div className="aisprawl-endpoints-type-left">
                <div className="aisprawl-endpoints-type-node" />
                <span className="aisprawl-endpoints-type-label">
                  {getGroupLabel(group)}
                </span>
                <div className="aisprawl-endpoints-type-connector" />
              </div>
              <div
                className={
                  isSingleEndpoint
                    ? "aisprawl-endpoints-type-devices aisprawl-endpoints-type-devices--single"
                    : "aisprawl-endpoints-type-devices"
                }
              >
                {group.endpoints.map((endpoint) => {
                  const isActive = selectedEndpoint === endpoint;

                  return (
                    <button
                      key={`${endpoint.deviceId}-${endpoint.appName}-${endpoint.bucket}`}
                      type="button"
                      className="aisprawl-endpoints-device-row aisprawl-endpoints-device-row-button"
                      onClick={() => setSelectedEndpoint(endpoint)}
                    >
                      <span className="aisprawl-endpoints-device-line" />
                      <span
                        ref={isActive ? activePillRef : undefined}
                        className={
                          isActive
                            ? "aisprawl-endpoints-device-pill aisprawl-endpoints-device-pill--active"
                            : "aisprawl-endpoints-device-pill"
                        }
                      >
                        {getEndpointLabel
                          ? getEndpointLabel(endpoint)
                          : endpoint.deviceName}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div
        ref={detailRef}
        className="aisprawl-endpoint-detail"
      >
        {selectedEndpoint ? (
          <>
            <div className="aisprawl-endpoint-detail-header">
              <span className="aisprawl-endpoint-detail-chip">
                {selectedEndpoint.deviceName}
              </span>
              <div className="aisprawl-endpoint-detail-subtitle">
                <span>{selectedEndpoint.appName}</span>
                <span className="aisprawl-endpoint-detail-dot">•</span>
                <span>{selectedEndpoint.publisher}</span>
              </div>
            </div>
            <div className="aisprawl-endpoint-detail-grid">
              <div className="aisprawl-endpoint-detail-row">
                <span className="aisprawl-endpoint-detail-label">
                  Endpoint ID
                </span>
                <span className="aisprawl-endpoint-detail-value">
                  {selectedEndpoint.deviceId}
                </span>
              </div>
              <div className="aisprawl-endpoint-detail-row">
                <span className="aisprawl-endpoint-detail-label">
                  Endpoint Name
                </span>
                <span className="aisprawl-endpoint-detail-value">
                  {selectedEndpoint.deviceName}
                </span>
              </div>
              <div className="aisprawl-endpoint-detail-row">
                <span className="aisprawl-endpoint-detail-label">
                  Platform
                </span>
                <span className="aisprawl-endpoint-detail-value">
                  {selectedEndpoint.edrSource}
                </span>
              </div>
              <div className="aisprawl-endpoint-detail-row">
                <span className="aisprawl-endpoint-detail-label">
                  Confidence
                </span>
                <span className="aisprawl-endpoint-detail-value aisprawl-endpoint-detail-confidence">
                  {(selectedEndpoint.confidence * 100).toFixed(0)}%
                </span>
              </div>
              <div className="aisprawl-endpoint-detail-row">
                <span className="aisprawl-endpoint-detail-label">
                  Action
                </span>
                <span
                  className={`aisprawl-endpoint-detail-value aisprawl-endpoint-detail-value--badge aisprawl-endpoint-detail-value--badge-${
                    (selectedEndpoint.action || "").toLowerCase()
                  }`}
                >
                  {selectedEndpoint.action}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="aisprawl-endpoint-detail-empty">
            <p>Select an endpoint pill to see details.</p>
          </div>
        )}
      </div>
    </div>
  );
}

